import { useState, useEffect } from "react";
import {
  BaseAssignmentResponseDto,
  BaseAssignmentResponseDtoStatus,
  PaginatedBaseAssignmentsResponseDto,
} from "@packages/api/madlenAPI.schemas";
import {
  getGetAssignmentsFromTeacherQueryKey,
  useEditAssignment,
  useGetAssignmentsFromTeacher,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import AllAssignmentsHeader from "../components/AllAssignmentsHeader";
import AllAssignmentsList from "../components/AllAssignmentsList";
import EmptyStateGuide from "../components/EmptyStateGuide";
import { Text } from "@core/text";

import Pagination from "@core/pagination";

const ITEMS_PER_PAGE = 10;

const Assignments = () => {
  const [activeFilter, setActiveFilter] =
    useState<BaseAssignmentResponseDtoStatus>();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    data,
    isLoading: isInitialLoading,
    error,
  } = useGetAssignmentsFromTeacher(
    {
      page: currentPage,
      size: ITEMS_PER_PAGE,
      status: activeFilter === undefined ? undefined : activeFilter,
    },
    {
      query: {},
    },
  );

  // Reset loading state when data changes
  useEffect(() => {
    if (data) {
      setIsLoadingMore(false);
    }
  }, [data]);

  const getAssignmentsQueryKey = getGetAssignmentsFromTeacherQueryKey({
    page: currentPage,
    size: ITEMS_PER_PAGE,
    status: activeFilter === undefined ? undefined : activeFilter,
  });

  const queryClient = useQueryClient();

  const updateAssignment = useEditAssignment({
    mutation: {
      onMutate: async (variables) => {
        const previousData = queryClient.getQueryData(getAssignmentsQueryKey);
        if (previousData) {
          queryClient.setQueryData(
            getAssignmentsQueryKey,
            (oldData: PaginatedBaseAssignmentsResponseDto) => ({
              ...oldData,
              data: oldData?.data?.map(
                (assignment: BaseAssignmentResponseDto) =>
                  assignment.id === variables.assignmentId
                    ? {
                        ...assignment,
                        name: variables.data.name,
                      }
                    : assignment,
              ),
            }),
          );
        }
        return { previousData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(getAssignmentsQueryKey, context?.previousData);
        console.error("Error updating assignment:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: getAssignmentsQueryKey });
      },
    },
  });

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    if (currentPage !== newPage) {
      setIsLoadingMore(true);
      setCurrentPage(newPage);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterChange = (
    newFilter: BaseAssignmentResponseDtoStatus | undefined,
  ) => {
    setCurrentPage(1);
    setActiveFilter(newFilter);
  };

  const isLoading = isInitialLoading || isLoadingMore;
  const showEmptyState =
    !isLoading &&
    !error &&
    !updateAssignment.isPending &&
    (!data?.data || data.data.length === 0);
  const showPagination =
    !isLoading && !error && data?.data && data.data.length > 0;

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <AllAssignmentsHeader
          activeFilter={activeFilter}
          setActiveFilter={handleFilterChange}
        />

        <AllAssignmentsList
          assignments={isInitialLoading ? [] : data?.data || []}
          isLoading={
            isInitialLoading || isLoadingMore || updateAssignment.isPending
          }
          error={error ?? undefined}
          updateAssignment={updateAssignment}
        />

        {showEmptyState && <EmptyStateGuide />}

        {showPagination && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default Assignments;
